import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, AlertCircle, ThumbsUp, Info, ChevronRight, CalendarCheck, Phone, MessageCircle } from "lucide-react";
import { CONTACT, ICONS, THEME, findService, servicesOf, doctorFor } from "@/data/site";
import { Reveal } from "@/components/common/Reveal";
import { FaqList } from "@/components/common/FaqList";
import { ServiceCard } from "@/components/common/ServiceCard";
import { AppointmentForm } from "@/components/common/AppointmentForm";
import { setBookingDoctor } from "@/components/common/bookingBus";
import NotFound from "./NotFound";

const Block = ({ title, en, children, tid }) => (
  <Reveal>
    <div data-testid={tid} className="rounded-[24px] border border-line bg-white p-6 md:p-8">
      <h2 className="font-display text-2xl text-ink">{title} <span className="font-en text-sm text-muted2">· {en}</span></h2>
      <div className="mt-5">{children}</div>
    </div>
  </Reveal>
);

const List = ({ items, icon: Icon, cls }) => (
  <ul className="grid gap-3 sm:grid-cols-2">
    {items.map((i) => (
      <li key={i.en} className="flex gap-3">
        <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${cls}`}><Icon className="h-3.5 w-3.5" /></span>
        <span><span className="block text-[15px] text-ink">{i.hi}</span><span className="font-en text-xs text-muted2">{i.en}</span></span>
      </li>
    ))}
  </ul>
);

const Sidebar = ({ s, t, d }) => (
  <aside className="space-y-4 lg:sticky lg:top-32">
    <div className={`rounded-[24px] p-6 text-white ${t.bg}`}>
      <div className="flex items-center gap-3">
        <img src={d.photo} alt={d.nameEn} className="h-14 w-14 rounded-xl object-cover object-top" />
        <div><p className="font-display text-xl">{d.nameHi}</p><p className="font-en text-xs text-white/75">{d.degreesEn}</p></div>
      </div>
      <a href="#appointment" onClick={() => setBookingDoctor(`${d.nameHi} / ${d.nameEn}`)} data-testid="service-sidebar-book" className="btn btn-light mt-5 w-full"><CalendarCheck className="h-4 w-4" /> अपॉइंटमेंट बुक करें</a>
      <a href={`tel:${CONTACT.phoneDial}`} className="btn btn-outline mt-2 w-full text-white"><Phone className="h-4 w-4" /> {CONTACT.phoneDisplay}</a>
      <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" className="btn btn-wa mt-2 w-full"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
    </div>
    <div className="rounded-[24px] border border-line bg-white p-5">
      <p className="text-sm font-semibold text-muted2">{t.labelHi} — अन्य सेवाएँ</p>
      <ul className="mt-3 space-y-1">
        {servicesOf(s.dept).filter((x) => x.slug !== s.slug).map((x) => (
          <li key={x.slug}><Link to={`/services/${x.slug}`} data-testid={`sidebar-link-${x.slug}`} className="flex items-center justify-between rounded-lg px-2 py-2 text-[15px] text-ink hover:bg-sand">{x.titleHi}<ChevronRight className="h-4 w-4 text-muted2" /></Link></li>
        ))}
      </ul>
    </div>
  </aside>
);

export default function ServiceDetail() {
  const { slug } = useParams();
  const s = findService(slug);
  if (!s) return <NotFound />;
  const t = THEME[s.dept];
  const d = doctorFor(s.dept);
  const Icon = ICONS[s.icon];
  const related = servicesOf(s.dept).filter((x) => x.slug !== s.slug).slice(0, 3);
  return (
    <div data-testid={`service-page-${s.slug}`}>
      <section className={`grain relative overflow-hidden ${s.dept === "surgery" ? "bg-surg-deep" : "bg-maroon-ink"}`}>
        <img src={s.image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="container-x relative py-14 md:py-20">
          <nav className="font-en flex flex-wrap items-center gap-1 text-xs text-white/60">
            <Link to="/" className="hover:text-white">Home</Link><ChevronRight className="h-3 w-3" />
            <Link to={t.path} data-testid="service-breadcrumb-dept" className="hover:text-white">{t.labelEn}</Link><ChevronRight className="h-3 w-3" />
            <span className="text-white">{s.titleEn}</span>
          </nav>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .7 }} className="mt-6 max-w-3xl">
            <span className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl text-white ${t.bg}`}><Icon className="h-6 w-6" /></span>
            <h1 data-testid="service-title" className="font-display mt-5 text-4xl leading-tight text-white sm:text-5xl lg:text-6xl">{s.titleHi}</h1>
            <p className="font-en mt-2 text-lg text-amber-300">{s.titleEn}</p>
            <p className="mt-4 text-lg text-white/80">{s.summaryHi}</p>
          </motion.div>
        </div>
      </section>
      <section className="py-14 md:py-20">
        <div className="container-x grid gap-8 lg:grid-cols-12">
          <div className="space-y-6 lg:col-span-8">
            <Block title="परिचय" en="Overview" tid="service-overview"><p className="text-[17px] leading-relaxed text-ink/85">{s.introHi}</p><p className="font-en mt-3 text-sm text-muted2">{s.introEn}</p></Block>
            <Block title="क्या-क्या शामिल है" en="What's included" tid="service-items"><List items={s.items} icon={Check} cls={`${t.tint} ${t.text}`} /></Block>
            <Block title="लक्षण — कब दिखाएँ" en="When to see a doctor" tid="service-symptoms"><List items={s.symptoms} icon={AlertCircle} cls="bg-amber-50 text-amber-700" /></Block>
            <div className="grid gap-6 md:grid-cols-2">
              <Block title="लाभ" en="Benefits" tid="service-pros"><ul className="space-y-3">{s.pros.map((p) => <li key={p.en} className="flex gap-2 text-[15px]"><ThumbsUp className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />{p.hi}</li>)}</ul></Block>
              <Block title="ध्यान रखें" en="Considerations" tid="service-cons"><ul className="space-y-3">{s.cons.map((p) => <li key={p.en} className="flex gap-2 text-[15px]"><Info className="mt-0.5 h-4 w-4 shrink-0 text-muted2" />{p.hi}</li>)}</ul></Block>
            </div>
            <Block title="इलाज की प्रक्रिया" en="Treatment steps" tid="service-steps">
              <ol className="grid gap-4 sm:grid-cols-2">
                {s.steps.map((st, i) => (
                  <li key={st.en} className="flex gap-3 rounded-2xl bg-sand p-4">
                    <span className={`font-en flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${t.bg}`}>{i + 1}</span>
                    <span><span className="block font-semibold text-ink">{st.hi}</span><span className="font-en text-xs text-muted2">{st.en}</span></span>
                  </li>
                ))}
              </ol>
            </Block>
            <Reveal><h2 className="font-display mb-4 mt-4 text-2xl text-ink">सवाल-जवाब <span className="font-en text-sm text-muted2">· FAQ</span></h2><FaqList faqs={s.faq} tid="service-faq" /></Reveal>
          </div>
          <div className="lg:col-span-4"><Sidebar s={s} t={t} d={d} /></div>
        </div>
      </section>
      <section className="bg-white py-16">
        <div className="container-x">
          <h2 className="font-display text-3xl text-ink">संबंधित सेवाएँ</h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{related.map((x, i) => <ServiceCard key={x.slug} s={x} i={i} />)}</div>
        </div>
      </section>
      <AppointmentForm defaultDept={s.dept} />
    </div>
  );
}
